import { CDataTable, CForm } from "@coreui/react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  BasicSelect,
  BasicTableContainer,
  DisplayRichText,
  LoadingSpinner,
  SaveButton,
  SquareButtonOutline,
} from "src/components";
import ControlledInput from "src/components/ControlledInput/ControlledInput";
import { useResizeObserver, useToasts } from "src/hooks";
import { ESG_EDIT_COLUMNS } from "../../constants";
import { useEsgContext } from "../../context";
import { createEsgEditRows } from "../../utils/create-rows";
import AddModal from "./AddModal";
import AddNewQuestion from "./AddNewQuestion";
import EditModal from "./EditModal";
import EditVersionMetadata from "./EditVersionMetadata";
import ManageVersions from "./ManageVersions";

const EMPTY_QUESTION = {
  label: "",
  labelExternal: "",
  key: "",
  weight: 1,
  answers: [
    { label: "", value: 0 },
    { label: "", value: 1 },
    { label: "", value: 2 },
    { label: "", value: 3 },
    { label: "", value: 4 },
  ],
  isNew: true,
};

const EditSurvey = () => {
  const [questionToEdit, setQuestionToEdit] = useState(null);
  const [isNew, setIsNew] = useState(false);

  const { width } = useResizeObserver();

  const isMobile = width < 1000;

  const { updateSurveyVersion, currentSurvey, setRefresh } = useEsgContext();

  const surveyAreas = currentSurvey?.areas || [];
  const currentVersionId = currentSurvey?.id;
  const isUsingGlobal = currentSurvey?.org === "global";

  const areas =
    surveyAreas?.map((area, idx) => ({
      label: area.label,
      value: idx,
    })) || [];

  const formMethods = useForm();
  const { reset, getValues, setValue, handleSubmit, control, trigger } =
    formMethods;

  const handleAdd = (areaIdx) => {
    const newIdx = surveyAreas[areaIdx].questions.length;
    setValue(`areas.${areaIdx}.questions.${newIdx}`, {
      ...EMPTY_QUESTION,
      key: `q${newIdx}`,
    });
    setIsNew(true);
    setQuestionToEdit(`areas.${areaIdx}.questions.${newIdx}`);
  };

  useEffect(() => {
    const surveyAreas = currentSurvey?.areas || [];
    const label = currentSurvey?.label || "";
    const description = currentSurvey?.description || "";
    if (!surveyAreas?.length) return;
    reset({ areas: surveyAreas, label, description });
  }, [currentSurvey]);

  const loading = !surveyAreas?.length;

  let rows = [];
  if (!loading) {
    rows = createEsgEditRows({ surveyAreas });
  }

  const handleEdit = (areaIdx, questionIdx) => {
    setIsNew(false);
    setQuestionToEdit(`areas.${areaIdx}.questions.${questionIdx}`);
  };

  const { tryAndNotify } = useToasts();

  const onSubmit = async (data) => {
    data.areas.forEach((area) => {
      area.questions = area.questions.filter(Boolean);
    });

    await tryAndNotify(
      async () => updateSurveyVersion({ data, currentVersionId }),
      "Survey updated successfully",
      "Error updating survey"
    );
  };

  return (
    <>
      <div>
        <ManageVersions currentVersionId={currentVersionId} />
        <h2 className="mt-4">Edit current version</h2>
        <EditVersionMetadata setValue={setValue} control={control} />
        <AddNewQuestion areas={areas} handleAdd={handleAdd} />
      </div>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <CForm onSubmit={handleSubmit(onSubmit)}>
          <BasicTableContainer>
            <CDataTable
              items={rows}
              fields={ESG_EDIT_COLUMNS}
              scopedSlots={{
                question: (item, index) => {
                  return (
                    <td>
                      <DisplayRichText value={item.question} />
                    </td>
                  );
                },
                weight: (item, index) => {
                  return (
                    <td>
                      {item.edit ? (
                        <ControlledInput
                          Component={BasicSelect}
                          options={[0, 1, 2, 3, 4, 5]}
                          name={item.questionName + ".weight"}
                          {...{ control, trigger, getValues }}
                        />
                      ) : (
                        <span>{item.weight}</span>
                      )}
                    </td>
                  );
                },
                edit: (item, index) => {
                  return (
                    <td>
                      {item.edit ? (
                        <SquareButtonOutline
                          style={{ width: "65px" }}
                          onClick={() =>
                            handleEdit(item.areaIdx, item.questionIdx)
                          }
                        >
                          Edit
                        </SquareButtonOutline>
                      ) : null}
                    </td>
                  );
                },
              }}
            />
          </BasicTableContainer>
          {isUsingGlobal && (
            <p className="text-danger mb-2">
              WARNING: You are editing a global version of the survey. It may be
              used by multiple organisations.
            </p>
          )}
          <SaveButton className="mt-3" />
        </CForm>
      )}
      <EditModal
        questionPath={questionToEdit}
        showModal={!!questionToEdit}
        closeModal={() => setQuestionToEdit("")}
        formMethods={formMethods}
        isNew={isNew}
        isUsingGlobal={isUsingGlobal}
        setRefresh={setRefresh}
        currentVersionId={currentVersionId}
      />
      <AddModal />
    </>
  );
};

export default EditSurvey;
